import { createClient } from '@supabase/supabase-js';
import type { Database } from '../integrations/supabase/types';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SUPABASE_SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Validate environment variables
if (!SUPABASE_URL) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

// Use service role key for development if available, otherwise use publishable key
const SUPABASE_KEY = SUPABASE_SERVICE_ROLE_KEY || SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_KEY) {
  throw new Error(
    'Missing Supabase key - either VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_SERVICE_ROLE_KEY must be set'
  );
}

console.log('🔑 Using Supabase key type:', SUPABASE_SERVICE_ROLE_KEY ? 'service_role' : 'anon');
console.log('🔧 Running on platform:', Capacitor.getPlatform());

// Check if we're running on a native platform
const isNative = Capacitor.isNativePlatform();

/**
 * Secure storage implementation using Capacitor Preferences for native apps
 * Falls back to localStorage for web
 */
const secureStorage = {
  getItem: async (key: string): Promise<string | null> => {
    if (isNative) {
      try {
        const { value } = await Preferences.get({ key });
        return value;
      } catch (error) {
        console.error('Error getting item from Preferences:', error);
        return null;
      }
    }
    // Web fallback
    return localStorage?.getItem(key) || null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (isNative) {
      try {
        await Preferences.set({ key, value });
      } catch (error) {
        console.error('Error setting item in Preferences:', error);
      }
    } else {
      // Web fallback
      localStorage?.setItem(key, value);
    }
  },
  removeItem: async (key: string): Promise<void> => {
    if (isNative) {
      try {
        await Preferences.remove({ key });
      } catch (error) {
        console.error('Error removing item from Preferences:', error);
      }
    } else {
      // Web fallback
      localStorage?.removeItem(key);
    }
  },
};

// Native-specific Supabase client configuration
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    // Use secure storage for native apps, localStorage for web
    storage: secureStorage,
    persistSession: true,
    autoRefreshToken: true,
    // For web apps we must allow the client to detect the session in the URL
    // after OAuth redirects so Supabase can parse the access token and create a session.
    // For native apps you may want to set this to false.
    detectSessionInUrl: !isNative,
  },
  realtime: {
    // Configure realtime for mobile
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Helper functions for Supabase operations
export const supabaseHelpers = {
  // Test connection
  testConnection: async (): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('flashcards')
        .select('count', { count: 'exact', head: true });
      return !error;
    } catch {
      return false;
    }
  },

  // Check authentication status
  isAuthenticated: async (): Promise<boolean> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return !!user;
  },
};
